"use client";

import { COMMUNITIES_API } from "@/lib/apis";
import { fetchData } from "@/lib/fetchData";
import { IGetCommunitiesToDiscoverResponseDTO, TDiscoverCommunity } from "@/types/community";
import { useQuery } from "@tanstack/react-query";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import CommunityCard from "./cards/community-card";
import DiscoverCommunitySearch from "./discover-communities-search";

const DiscoverCommunities = () => {
	const [filteredCommunities, setFilteredCommunities] = useState<TDiscoverCommunity[]>([]);

	const { isLoading, data } = useQuery<IGetCommunitiesToDiscoverResponseDTO>({
		queryKey: ["discover-communities"],
		queryFn: () => fetchData(COMMUNITIES_API),
	});

	const handleSearch = (query: string) => {
		const results = filteredCommunities.filter((community) =>
			community.name.toLowerCase().includes(query?.toLowerCase())
		);

		if (!query) {
			const communities = data?.data || [];

			setFilteredCommunities(communities);
			return;
		}

		setFilteredCommunities(results);
	};

	useEffect(() => {
		const communities = data?.data || [];

		setFilteredCommunities(communities);
	}, [data]);

	return (
		<>
			<div className="mt-12 items-center justify-center gap-2 md:mx-auto md:px-12">
				<div className="my-4 flex flex-col items-center justify-center gap-1 md:gap-2">
					<Image
						src="/images/communities/discover-heading.svg"
						alt="Discover Communities"
						className="h-auto w-full object-contain md:w-96"
						width={1080}
						height={1080}
					/>

					<h1 className="sr-only text-3xl font-bold text-gray-900 md:text-4xl">
						Discover Communities
					</h1>

					<p className="text-sm font-semibold md:text-base">
						or{" "}
						<Link href="/contact-us" target="_blank" className="text-brand-primary">
							Create your own
						</Link>
					</p>
				</div>

				<p className="mx-auto my-2 max-w-3xl px-4 text-xs font-semibold italic text-brand-primary/80">
					100+ Communities / 5K+ Members
				</p>

				<DiscoverCommunitySearch onChange={handleSearch} />

				{isLoading ? (
					<div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6 lg:grid-cols-3 3xl:grid-cols-4">
						<Skeleton className="h-48 w-full" />
						<Skeleton className="h-48 w-full" />
						<Skeleton className="h-48 w-full" />
					</div>
				) : filteredCommunities.length > 0 ? (
					<div className="mt-6 grid grid-cols-1 gap-4 md:mt-8 md:grid-cols-2 md:gap-6 lg:grid-cols-3 3xl:grid-cols-4">
						{filteredCommunities.map((community) => (
							<CommunityCard
								key={community.id}
								communityCardInfo={{
									...community,
									totalMembers: community.totalMembers,
									logo: community.logo || "/images/default-placeholder-image.png",
									coverImage: community.coverImage || "",
									name: community.name || "",
									description: community.description || "",
									moderators: community.moderators || [],
								}}
							/>
						))}
					</div>
				) : (
					<p className="mx-auto py-6 text-center text-gray-500">No communities found</p>
				)}
			</div>
		</>
	);
};
export default DiscoverCommunities;
